import { FC, useEffect } from 'react';
import { getZakekeToken } from '../../api/zakeke';
import { addCustomProduct, BrochureProduct } from '../../api/brochures';
import { getRequestedLanguages } from '../../locale/languages';

/** Key-value pair for a selected product attribute (e.g. size, color) */
type ZakekeSelectedAttribute = Record<string, string>;

type ZakekeDesigner = Record<string, unknown> & {
    createIframe: (config: Record<string, unknown>) => Promise<undefined>;
    removeIframe: () => void;
};

/** Data passed to `addToCart` and `editAddToCart` callbacks */
interface ZakekeAddToCartData {
    /** Product identifier in the ecommerce platform */
    productId: string;
    /** Unique Zakeke design ID — use this server-side via the Cart/Designs API */
    designid: string;
    /** Quantity of the customized product */
    quantity: number;
    /** Selected product attributes */
    selectedattributes: ZakekeSelectedAttribute[];
    /** Optional arbitrary additional data */
    additionaldata: Record<string, unknown>;
}

interface Props {
    product: BrochureProduct;
    designId?: string;
    brochureBasketId: string;
    onClose: () => void;
    onError: (message: string) => void;
}

const ZakekeViewer: FC<Props> = ({ product, designId, brochureBasketId, onClose, onError }) => {
    const language = getRequestedLanguages(window.errea_accountUserData.language);

    const handleAddToCart = async (data: ZakekeAddToCartData) => {
        console.log('data', data);
        try {
            await addCustomProduct(brochureBasketId, product.id, data.designid);
            onClose();
        } catch (err) {
            onClose();
            onError(typeof err === 'string' ? err : 'Errore durante il salvataggio del prodotto personalizzato.');
        }
    };

    useEffect(() => {
        let script: HTMLScriptElement;
        let customizer: ZakekeDesigner;
        const load = async () => {
            if (document.getElementById('zakeke-customizer')) return;
            script = document.createElement('script');
            const zakekeTokenResponse = await getZakekeToken();
            script.id = 'zakeke-customizer';
            script.src = 'https://portal.zakeke.com/scripts/integration/apiV2/customizer.js';
            script.async = true;

            script.onload = () => {
                customizer = new (window as any).ZakekeDesigner();

                const config = {
                    // Auth
                    tokenOauth: zakekeTokenResponse.access_token,
                    productId: product.ecommerce_id.split('/').pop(),
                    // productId: '8981068972323',
                    productName: product.name,
                    designId,
                    getProductInfo: () => {
                        return { price: 0, isOutOfStock: false };
                    },
                    getProductPrice: () => {
                        return { price: 0, isOutOfStock: false };
                    },
                    getProductAttribute: () => {
                        console.log('[Zakeke] getProductAttribute called');
                    },
                    // Add to cart
                    cartButtonText: language.brochures.zakeke.add_to_brochure,
                    addToCart: (data: ZakekeAddToCartData) => {
                        handleAddToCart(data);
                    },
                    editAddToCart: (data: ZakekeAddToCartData) => {
                        handleAddToCart(data);
                    },
                    // Names & numbers
                    isNamesAndNumbers: false,
                    // Bulk variations
                    isMultipleVariations: false,
                    // Save design
                    isSaveDesign: true,
                    getSaveDesign: (data: any) => {
                        console.log('[Zakeke] getSaveDesign called', data);
                    },
                    // Shared design
                    isSharedDesignUrl: false,
                    // Back button
                    onBackClicked: onClose,
                    // Misc
                    hideVariants: false,
                    inventoryManagement: false,
                    priceTaxIncluded: false,
                    labelTax: 'hidden',
                    isClientPreviews: true,
                    mobileVersion: false,
                    culture: window?.Shopify?.locale ?? 'it-IT',
                    currency: window?.Shopify?.currency?.active ?? 'EUR',
                    quantity: 1,
                    additionalData: {},
                    selectedAttributes: {},
                };

                customizer.createIframe(config);
            };

            document.body.appendChild(script);
        };
        load();

        return () => {
            if (script) script.remove();
            if (customizer) customizer.removeIframe();
        };
    }, []);

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 999,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    width: '60dvw',
                    height: '90dvh',
                    backgroundColor: '#fff',
                    overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div id="zakeke-container" style={{ width: '100%', height: '100%' }}>
                    <style>{`#zakeke-container iframe { width: 100%; height: 100%; }`}</style>
                </div>
            </div>
        </div>
    );
};

export default ZakekeViewer;
